import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Instant-render data hook.
 * 1. Reads localStorage cache synchronously -> initial paint has data (no spinner).
 * 2. Fetches in the background and updates only when data actually changed.
 * 3. Optionally re-polls every `pollMs` without any full-page reload.
 */
export function useCachedData(cacheKey, fetcher, { pollMs = 0 } = {}) {
  const readCache = () => {
    try {
      const raw = localStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const [data, setData] = useState(readCache);
  const [loading, setLoading] = useState(!data);
  const [stale, setStale] = useState(false);
  const mounted = useRef(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async () => {
    try {
      const fresh = await fetcherRef.current();
      if (!mounted.current) return;
      setData((prev) => {
        const changed = JSON.stringify(prev) !== JSON.stringify(fresh);
        if (changed) {
          try {
            localStorage.setItem(cacheKey, JSON.stringify(fresh));
          } catch {}
          return fresh;
        }
        return prev;
      });
      setStale(false);
      setLoading(false);
    } catch (e) {
      if (!mounted.current) return;
      setStale(true); // keep showing cached data
      setLoading(false);
    }
  }, [cacheKey]);

  useEffect(() => {
    mounted.current = true;
    load();
    let id;
    if (pollMs > 0) id = setInterval(load, pollMs);
    return () => {
      mounted.current = false;
      if (id) clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, pollMs]);

  return { data, loading, stale, refresh: load };
}
